import { enums } from "@shared/constants";
import { SeatingLocationMapIdType } from "@shared/constants/enums/enums-types";
import Konva from "konva";
import SeatingService from "../services/SeatingService";
import {
  PublicBaseLocationSerializer,
  TransformType,
  PointType,
} from "../types/seating-types";
import GeneralAdmissionFactory from "./GeneralAdmissionFactory";
import RowFactory from "./RowFactory";
import SeatFactory from "./SeatFactory";
import SeatingUtilsFactory from "./SeatingUtilsFactory";

type BuildersType =
  | RowFactory["create"]
  | RowFactory["createSquareTable"]
  | RowFactory["createRoundTable"]
  | RowFactory["createSemiRoundTable"]
  | GeneralAdmissionFactory["create"]
  | (() => { groupActive?: Konva.Group; groupDisabled?: Konva.Group });

export default class LocationFactory {
  public rowFactory: RowFactory;

  public generalAdmissionFactory: GeneralAdmissionFactory;

  constructor(
    seatingService: SeatingService,
    generalAdmissionFactory: GeneralAdmissionFactory,
    seatFactory: SeatFactory
  ) {
    this.rowFactory = new RowFactory(seatingService, seatFactory);
    this.generalAdmissionFactory = generalAdmissionFactory;
  }

  getBuilder(type: SeatingLocationMapIdType): BuildersType {
    const locationTypes = enums.SEATING_LOCATION_TYPE_MAP;
    const builders: { [key: string]: BuildersType } = {
      [locationTypes.TYPE_ROW.id]: this.rowFactory.create,
      [locationTypes.TYPE_SQUARE_TABLE.id]: this.rowFactory.createSquareTable,
      [locationTypes.TYPE_TABLE.id]: this.rowFactory.createRoundTable,
      [locationTypes.TYPE_SEMI_ROUND_TABLE.id]:
        this.rowFactory.createSemiRoundTable,
      [locationTypes.TYPE_GENERAL_ADMISSION.id]:
        this.generalAdmissionFactory.create,
      default: () => {
        return { groupActive: undefined, groupDisabled: undefined };
      },
    };

    return builders[type] || builders.default;
  }

  createLocation(
    location: PublicBaseLocationSerializer,
    transform: TransformType,
    seatSize: number,
    seatSpacing: number,
    segmentTransform: TransformType,
    hideLocationNames: boolean
  ): {
    groupActive?: Konva.Group;
    groupDisabled?: Konva.Group;
  } {
    const params = {
      location,
      transform,
      seatSize,
      seatSpacing,
      segmentTransform,
      hideLocationNames,
    };
    return this.getBuilder(location.type)(params);
  }

  // gets active and disabled areas on the seating map
  create(
    locationSet: PublicBaseLocationSerializer[],
    display: { points: PointType[] },
    identifier: string,
    { x = 0, y = 0, rotation = 0 }: TransformType,
    seatSize: number,
    seatSpacing: number,
    hideLocationNames: boolean,
    hideSegmentBackground: boolean
  ): {
    locationGroupActive: Konva.Group;
    locationGroupDisabled: Konva.Group;
  } {
    const container = new Konva.Line({
      points: display.points.flatMap((point: number[]) => point),
      fill: "#f4f4f4",
      closed: true,
      perfectDrawEnabled: false,
      listening: false,
      opacity: hideSegmentBackground ? 0 : 1, // hides the gray square container behind the location
    });

    const locationGroupActive = new Konva.Group({
      x,
      y,
      draggable: false,
      name: "location",
      identifier,
    });

    const locationGroupDisabled = new Konva.Group({
      x,
      y,
      draggable: false,
      name: "location_disabled",
      identifier,
    });

    locationGroupDisabled.add(container);

    // based on the locationData input, create the location based on its type
    // this could be a row, table, GA location, etc
    locationSet?.forEach((locationData) => {
      const location = this.createLocation(
        locationData,
        {
          x: locationData.transform.x + x,
          y: locationData.transform.y + y,
          rotation: Number(locationData.transform.rotation),
        },
        seatSize,
        seatSpacing,
        { x, y, rotation: Number(rotation) },
        hideLocationNames
      );

      // if the location is active, add it to the active group
      if (location?.groupActive?.hasChildren()) {
        locationGroupActive.add(location.groupActive);
      }
      // if the location is disabled, add it to the disabled group
      if (location?.groupDisabled?.hasChildren()) {
        locationGroupDisabled.add(location.groupDisabled);
      }
    });

    SeatingUtilsFactory.rotateAroundCenter(locationGroupActive, rotation ?? 0);
    SeatingUtilsFactory.rotateAroundCenter(
      locationGroupDisabled,
      rotation ?? 0
    );

    const groupActiveBounds = locationGroupActive.getClientRect();
    locationGroupActive.width(groupActiveBounds.width);
    locationGroupActive.height(groupActiveBounds.height);
    locationGroupActive.setAttr("visibleGroupOffset", {
      x: groupActiveBounds.x - x,
      y: groupActiveBounds.y - y,
    });

    const groupDisabledBounds = locationGroupDisabled.getClientRect();
    locationGroupDisabled.width(groupDisabledBounds.width);
    locationGroupDisabled.height(groupDisabledBounds.height);
    locationGroupDisabled.setAttr("visibleGroupOffset", {
      x: groupDisabledBounds.x - x,
      y: groupDisabledBounds.y - y,
    });

    return { locationGroupActive, locationGroupDisabled };
  }
}
